import { CardComponent } from '@ui/card/card.component';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  Assignment,
  AssignmentMontage,
  AssignmentService,
  Invoice,
} from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { AppService } from '../../../app.service';
import { LoadingService } from '@ui/app-layout/loading-service';
import { finalize } from 'rxjs';
import { tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@ui/button/button.component';
import { IconComponent } from '@ui/icon/icon.component';
import { AssignmentsMontageComponent } from '../assignments-montage/assignments-montage.component';
import { AssignmentsServiceComponent } from '../assignments-service/assignments-service.component';
import { AssignmentsTimelineComponent } from '../assignments-timeline/assignments-timeline.component';
import { AssignmentsComplaintsComponent } from '../assignments-complaints/assignments-complaints.component';
import { afasContactDisplayName } from '@api/models/customer.model';

@Component({
  selector: 'app-assignments-detail',
  standalone: true,
  imports: [
    CardComponent,
    CommonModule,
    ButtonComponent,
    RouterLink,
    IconComponent,
    AssignmentsMontageComponent,
    AssignmentsServiceComponent,
    AssignmentsTimelineComponent,
    AssignmentsComplaintsComponent,
  ],
  templateUrl: './assignments-detail.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentsDetailComponent {
  assignmentsService = inject(AssignmentsService);
  route = inject(ActivatedRoute);
  loadingService = inject(LoadingService);
  appService = inject(AppService);
  router = inject(Router);

  assignment = signal<Assignment | null>(null);
  invoices = signal<Invoice[] | null>(null);
  loadingDetails = signal(true);
  loadingInvoices = signal(true);

  hasRequest = computed(() => this.assignment()?.request !== null);

  contactDisplayName = computed(() =>
    afasContactDisplayName(this.assignment()?.details)
  );

  constructor() {
    this.loadingService.show();
    this.getAssignment();
  }

  getAssignment() {
    this.assignmentsService
      .getAssignment(this.route.snapshot.params['id'])
      .pipe(
        tap(() => this.loadingDetails.set(true)),
        finalize(() => this.loadingDetails.set(false))
      )
      .subscribe((assignment) => {
        this.assignment.set(assignment);
        this.appService.setCurrentMunicipalityId(assignment.municipality_id);
        this.getInvoices();
        this.loadingService.hide();
      });
  }

  getInvoices() {
    this.loadingInvoices.set(true);
    const assignmentId = this.assignment()?.id;
    const personID = this.assignment()?.details?.id;
    if (assignmentId && personID) {
      this.assignmentsService
        .getInvoices(assignmentId, personID)
        .pipe(finalize(() => this.loadingInvoices.set(false)))
        .subscribe((invoices) => {
        this.invoices.set(invoices);
      });
    }
  }

  downloadInvoice(invoice: Invoice) {
    this.loadingService.show();
    this.assignmentsService.downloadInvoice(this.assignment()?.id ?? '', invoice.number, this.assignment()?.details?.id ?? '').subscribe((response) => {
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      this.loadingService.hide();
    });
  }

  goToRequest() {
    this.router.navigate(['/assignments', 'request', this.assignment()?.request?.id]);
  }
}
